<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8">
    <div class="max-w-6xl mx-auto">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 class="text-3xl font-black text-white">{{ t('adminSections.title') }}</h1>
          <p class="text-white/70">{{ t('adminSections.subtitle') }}</p>
        </div>
        <div class="flex gap-3">
          <router-link
            to="/admin/dashboard"
            class="px-4 py-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            {{ t('common.dashboard') }}
          </router-link>
          <button
            @click="handleLogout"
            class="px-4 py-2 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-colors"
          >
            {{ t('common.logout') }}
          </button>
        </div>
      </div>

      <div class="grid lg:grid-cols-2 gap-6">
        <div class="bg-white/10 border border-white/20 rounded-2xl p-6">
          <h2 class="text-2xl font-bold text-white mb-4">
            {{ editingSection ? t('adminSections.formTitleEdit') : t('adminSections.formTitleCreate') }}
          </h2>

          <form @submit.prevent="handleSubmit" class="space-y-4">
            <div>
              <label class="block text-white/80 mb-2">{{ t('adminSections.sectionName') }} *</label>
              <input
                v-model="form.name"
                type="text"
                required
                :placeholder="t('adminSections.sectionNamePlaceholder')"
                class="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 outline-none focus:border-purple-400"
              />
            </div>

            <div>
              <label class="block text-white/80 mb-2">{{ t('adminSections.groups') }} *</label>
              <textarea
                v-model="form.groupsText"
                required
                rows="7"
                :placeholder="t('adminSections.groupsPlaceholder')"
                class="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 outline-none focus:border-purple-400 resize-none"
              ></textarea>
              <p class="text-white/60 text-sm mt-2">{{ t('adminSections.groupsHint') }}</p>
            </div>

            <div class="flex gap-3">
              <button
                type="submit"
                :disabled="saving"
                class="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold hover:from-purple-600 hover:to-pink-600 disabled:opacity-60"
              >
                {{ saving ? t('adminSections.saving') : editingSection ? t('adminSections.update') : t('common.add') }}
              </button>
              <button
                v-if="editingSection"
                type="button"
                @click="resetForm"
                class="px-6 py-3 rounded-xl bg-gray-600 text-white hover:bg-gray-700"
              >
                {{ t('common.cancel') }}
              </button>
            </div>
          </form>
        </div>

        <div class="bg-white/10 border border-white/20 rounded-2xl p-6">
          <h2 class="text-2xl font-bold text-white mb-4">{{ t('adminSections.listTitle') }}</h2>

          <div v-if="loading" class="text-white/80">{{ t('common.loading') }}</div>
          <div v-else-if="error" class="text-red-300">{{ error }}</div>

          <div v-else class="space-y-3 max-h-[600px] overflow-y-auto pr-1">
            <div
              v-for="item in sections"
              :key="item._id"
              class="bg-white/10 border border-white/10 rounded-xl p-4"
            >
              <h3 class="text-white font-bold text-lg">{{ item.name }}</h3>
              <p class="text-white/70 text-sm mt-1">{{ item.groups.length }} {{ t('adminSections.groupsCount') }}</p>

              <div class="flex flex-wrap gap-2 mt-3">
                <span
                  v-for="group in item.groups"
                  :key="group"
                  class="px-2 py-1 rounded-lg bg-purple-500/30 text-white text-xs"
                >
                  {{ group }}
                </span>
              </div>

              <div class="flex gap-2 mt-4">
                <button
                  @click="editSection(item)"
                  class="px-3 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600"
                >
                  {{ t('adminSections.edit') }}
                </button>
                <button
                  @click="deleteSection(item._id)"
                  class="px-3 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600"
                >
                  {{ t('adminSections.delete') }}
                </button>
              </div>
            </div>

            <p v-if="sections.length === 0" class="text-white/70">{{ t('adminSections.noData') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import sectionsApi from '@/services/sectionsApi';
import { authStore } from '@/stores/authStores';

export default {
  name: 'AdminSections',
  setup() {
    const router = useRouter();
    const { t } = useI18n();
    const sections = ref([]);
    const loading = ref(false);
    const saving = ref(false);
    const error = ref('');
    const editingSection = ref(null);

    const form = ref({
      name: '',
      groupsText: '',
    });

    const parseGroups = (text) =>
      text
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);

    const resetForm = () => {
      form.value = { name: '', groupsText: '' };
      editingSection.value = null;
    };

    const fetchSections = async () => {
      loading.value = true;
      error.value = '';
      try {
        const res = await sectionsApi.get('/api/sections');
        sections.value = Array.isArray(res.data) ? res.data : [];
      } catch (err) {
        error.value = err.response?.data?.message || t('adminSections.loadError');
        sections.value = [];
      } finally {
        loading.value = false;
      }
    };

    const handleSubmit = async () => {
      const payload = {
        name: form.value.name.trim(),
        groups: parseGroups(form.value.groupsText),
      };

      if (!payload.name || payload.groups.length === 0) {
        alert(t('adminSections.fillError'));
        return;
      }

      saving.value = true;
      try {
        if (editingSection.value) {
          await sectionsApi.put(`/api/sections/${editingSection.value._id}`, payload);
          alert(t('adminSections.updateSuccess'));
        } else {
          await sectionsApi.post('/api/sections', payload);
          alert(t('adminSections.createSuccess'));
        }
        resetForm();
        await fetchSections();
      } catch (err) {
        alert(err.response?.data?.message || t('adminSections.saveError'));
      } finally {
        saving.value = false;
      }
    };

    const editSection = (item) => {
      editingSection.value = item;
      form.value = {
        name: item.name,
        groupsText: (item.groups || []).join('\n'),
      };
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const deleteSection = async (id) => {
      if (!confirm(t('adminSections.deleteConfirm'))) return;

      try {
        await sectionsApi.delete(`/api/sections/${id}`);
        await fetchSections();
      } catch (err) {
        alert(err.response?.data?.message || t('adminSections.deleteError'));
      }
    };

    const handleLogout = () => {
      authStore.logout();
      router.push('/admin');
    };

    onMounted(async () => {
      if (!authStore.isAuth()) {
        router.push('/admin');
        return;
      }
      await fetchSections();
    });

    return {
      sections,
      loading,
      saving,
      error,
      form,
      editingSection,
      handleSubmit,
      editSection,
      deleteSection,
      resetForm,
      handleLogout,
      t,
    };
  },
};
</script>

